import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, imageUrl } from "../lib/api";
import { ArrowRight, Hammer, Truck, Heart } from "lucide-react";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [sf, setSf] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/storefront").then((r) => setSf(r.data.images)).catch(() => {});
  }, []);

  const heroImg = sf?.hero_bg;

  const tees = products.filter((p) => p.category === "tshirt");
  const tumblers = products.filter((p) => p.category === "tumbler");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={heroImg ? { backgroundImage: `url('${imageUrl(heroImg)}')` } : undefined}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-[var(--navy)]/55 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy)]/80 via-[var(--navy)]/30 to-transparent" />
        <div className="absolute inset-0 stripes-bg" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 text-[var(--bone)]">
            <div className="inline-flex items-center gap-2 stamp-badge bg-[var(--bone)]/15 border-[var(--bone)]/40 text-[var(--bone)]">
              <Heart className="w-3 h-3" /> A FAMILY SHOP · SINCE 2019
            </div>
            <h1 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mt-6 text-balance">
              {sf?.text?.hero_heading ?? "A little family shop started with MawMaw's love of crafting and the need to work from home."}
              <span className="block text-[var(--gold)] text-2xl md:text-3xl lg:text-4xl mt-4">
                {sf?.text?.hero_subheading ?? "Papa G helps out with the shirts and tumblers when he's home."}
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[var(--bone)]/85 leading-relaxed">
              {sf?.text?.hero_description ?? "Crafted in a home shop in East Texas."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="btn-primary btn-crimson" data-testid="hero-shop-button">
                Shop the Goods <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop" className="btn-ghost !border-[var(--bone)] !text-[var(--bone)] hover:!bg-[var(--bone)] hover:!text-[var(--navy)]" data-testid="hero-tumbler-button">
                Customize Products
              </Link>
            </div>
            <p className="mt-4 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--bone)]/70">
              Add your own design or name · or pick from our preset designs
            </p>
          </div>

          <div className="lg:col-span-4">
            <Link to="/shop?category=handmade" className="group block relative overflow-hidden rounded-sm aspect-[3/4] border border-white/20" data-testid="hero-handmade-card">
              <img src={imageUrl(sf?.handmade_card)} alt="MawMaw's handmade crafts" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)] via-[var(--navy)]/40 to-transparent"/>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-[var(--bone)]">
                <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--gold)]">MADE BY HAND</div>
                <div className="font-serif-display text-3xl mt-1 leading-tight">MawMaw&apos;s<br/>Handmade</div>
                <div className="inline-flex items-center gap-2 mt-3 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--bone)] group-hover:text-[var(--gold)] transition">
                  Shop her pieces <ArrowRight className="w-3.5 h-3.5"/>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES — Bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">02 — WHAT WE MAKE</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] mt-2">Pressed with pride.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-mono-ui text-xs tracking-[0.25em] text-[var(--navy)] hover:text-[var(--crimson)]" data-testid="see-all-link">
            SEE ALL <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          <Link to="/shop?category=tshirt" className="md:col-span-3 group relative aspect-[16/10] overflow-hidden rounded-sm" data-testid="category-tshirts">
            <img src={imageUrl(sf?.category_tshirt)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/85 via-[var(--navy)]/20 to-transparent"/>
            <div className="absolute bottom-0 left-0 p-8 text-[var(--bone)]">
              <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--gold)]">/01</div>
              <div className="font-serif-display text-4xl md:text-5xl mt-2">Custom Tees</div>
              <div className="font-mono-ui text-xs tracking-[0.2em] mt-2 opacity-80">FROM $20</div>
            </div>
          </Link>
          <Link to="/shop?category=tumbler" className="md:col-span-2 group relative aspect-[16/10] md:aspect-auto overflow-hidden rounded-sm" data-testid="category-tumblers">
            <img src={imageUrl(sf?.category_tumbler)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--crimson)]/80 via-[var(--crimson)]/10 to-transparent"/>
            <div className="absolute bottom-0 left-0 p-8 text-[var(--bone)]">
              <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--gold)]">/02</div>
              <div className="font-serif-display text-4xl md:text-5xl mt-2">Custom Tumblers</div>
              <div className="font-mono-ui text-xs tracking-[0.2em] mt-2 opacity-80">FROM $25</div>
            </div>
          </Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="mb-10">
          <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">03 — REGULARS&apos; FAVORITES</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] mt-2">What folks keep ordering.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...tees.slice(0,2), ...tumblers.slice(0,2)].map((p) => (
            <Link to={`/product/${p.id}`} key={p.id} className="group" data-testid={`featured-product-${p.id}`}>
              <div className="aspect-square overflow-hidden rounded-sm bg-[var(--bone-2)] border border-[rgba(27,42,65,0.1)]">
                <img src={imageUrl(p.mockup_image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <div className="font-serif-display text-xl text-[var(--navy)]">{p.name}</div>
                  <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mt-1">{p.category === "tshirt" ? "Custom Tee" : "Custom Tumbler"}</div>
                </div>
                <div className="font-mono-ui text-sm text-[var(--crimson)]">${p.base_price.toFixed(0)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="p-4 bg-[var(--navy)] rounded-sm text-[var(--bone)]">
                <Hammer className="w-5 h-5 text-[var(--gold)]" />
                <div className="font-mono-ui text-[10px] tracking-[0.25em] mt-3 opacity-70">CRAFTED</div>
                <div className="font-serif-display text-2xl mt-1">By hand</div>
              </div>
              <div className="p-4 bg-[var(--navy)] rounded-sm text-[var(--bone)]">
                <Truck className="w-5 h-5 text-[var(--gold)]" />
                <div className="font-mono-ui text-[10px] tracking-[0.25em] mt-3 opacity-70">SHIPPED</div>
                <div className="font-serif-display text-2xl mt-1">In 5 days</div>
              </div>
              <div className="p-4 bg-[var(--navy)] rounded-sm text-[var(--bone)] col-span-2">
                <div className="font-mono-ui text-[10px] tracking-[0.25em] opacity-70">PRESSED ON</div>
                <div className="font-serif-display text-2xl mt-1">Heavyweight cotton · stainless steel</div>
              </div>
            </div>
            <div className="aspect-[4/5] rounded-sm overflow-hidden">
              <img src={imageUrl(sf?.story_photo)} alt="Papa G and MawMaw" className="w-full h-full object-cover object-top"/>
            </div>
            <div className="absolute -bottom-4 -right-4 hidden md:block stamp-badge bg-[var(--gold)] border-[var(--gold-warm)] text-[var(--navy)]">
              ♥ PAPA G &amp; MAWMAW · EST. 2000
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">04 — OUR STORY</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] mt-3 leading-tight">
              Built from the kitchen table.<br/>Run on love and second cups of coffee &amp; ice tea.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]">
              Papa G encouraged MawMaw to start this shop in 2019 after her latest back
              surgery — somewhere she could work from home at her own pace as she recovered.
              Papa G drives a truck full time and is home daily to help out with the presses.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-[var(--ink-soft)]">
              Between the two of them they&apos;ve got 5 kids and 7 grandkids running
              around — and this little shop gives MawMaw the time and space to be with
              them. Every order you place keeps that going.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <div>
                <div className="font-serif-display text-4xl text-[var(--crimson)]">5</div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] mt-1 text-[var(--ink-soft)]">KIDS</div>
              </div>
              <div>
                <div className="font-serif-display text-4xl text-[var(--crimson)]">7</div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] mt-1 text-[var(--ink-soft)]">GRANDKIDS</div>
              </div>
              <div>
                <div className="font-serif-display text-4xl text-[var(--crimson)]">100%</div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] mt-1 text-[var(--ink-soft)]">FAMILY RUN</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
